// EP-135 Tier 1 — Committee registry. Lists active members, badges
// for "no longer qualifying" (D-11a), an end-term admin-only action,
// and an admin+ Create button. Staff sees the list + Edit only.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, Plus, X } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Button, Select, Badge, EmptyState, Skeleton } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useCommitteeMembers, useEndCommitteeTerm } from '@/hooks/useGovernance'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { CommitteeMemberFormModal } from './components/CommitteeMemberFormModal'
import { EndTermModal } from './components/EndTermModal'
import type { CommitteeMemberSummary } from '@/types/governance'

export function CommitteePage() {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')
  const formatDate = useDateFormatter()

  const [active, setActive] = useState<'true' | ''>('true')
  const [createOpen, setCreateOpen] = useState(false)
  const [endingMember, setEndingMember] = useState<CommitteeMemberSummary | null>(null)

  const { data, isLoading } = useCommitteeMembers({ active: active === 'true' ? true : undefined })
  const endTerm = useEndCommitteeTerm()

  const canCreate = user?.role === 'admin' || user?.role === 'owner'
  const canEndTerm = user?.role === 'admin' || user?.role === 'owner'

  const handleEndTerm = async (reason: string, ended_on?: string) => {
    if (!endingMember) return
    try {
      await endTerm.mutateAsync({ id: endingMember.id, reason, ended_on })
      addToast({ type: 'success', message: t('committee.messages.term_ended') })
      setEndingMember(null)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[ { label: t('committee.title') } ]} />

        <PageHeader
          title={t('committee.title')}
          description={t('committee.description')}
          primaryAction={
            canCreate
              ? {
                  key: 'new-member',
                  label: t('committee.actions.new_member'),
                  icon: Plus,
                  onClick: () => setCreateOpen(true),
                  testId: 'btn-new-member',
                }
              : undefined
          }
        />

        <Card bodyClassName="space-y-4">
          <Select
            label={tc('labels.status')}
            value={active}
            onChange={(e) => setActive(e.target.value as 'true' | '')}
            options={[
              { value: 'true', label: t('committee.filters.active') },
              { value: '', label: t('committee.filters.show_active') + ' + ' + t('committee.filters.ended') },
            ]}
            data-testid="filter-active"
          />

          {isLoading ? (
            <div className="space-y-3">
              {[ ...Array(5) ].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : !data?.data || data.data.length === 0 ? (
            <EmptyState
              icon={<Users className="w-12 h-12" />}
              title={t('committee.empty.no_members')}
              description={t('committee.empty.no_members_description')}
              action={
                canCreate ? (
                  <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
                    {t('committee.actions.new_member')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('committee.labels.customer')}</th>
                    <th>{t('committee.labels.role')}</th>
                    <th>{t('committee.labels.term_starts_on')}</th>
                    <th>{t('committee.labels.term_ends_on')}</th>
                    <th>{tc('labels.status')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((m) => (
                    <tr key={m.id} className="hover:bg-base-200">
                      <td className="font-medium">{m.customer_name}</td>
                      <td>{t(`committee.roles.${m.role}`)}</td>
                      <td>{m.term_starts_on ? formatDate(m.term_starts_on) : '—'}</td>
                      <td>{m.term_ends_on ? formatDate(m.term_ends_on) : '—'}</td>
                      <td>
                        {m.ended_on ? (
                          <Badge variant="count">{t('committee.labels.ended_on')}</Badge>
                        ) : m.qualifies ? (
                          <Badge variant="primary">{t('committee.labels.qualifies')}</Badge>
                        ) : (
                          <Badge variant="warning">{t('committee.labels.not_qualifying')}</Badge>
                        )}
                      </td>
                      <td>
                        {canEndTerm && !m.ended_on && (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<X className="w-4 h-4" />}
                            onClick={() => setEndingMember(m)}
                            data-testid={`btn-end-term-${m.id}`}
                          >
                            {t('committee.actions.end_term')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <CommitteeMemberFormModal
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false)
          addToast({ type: 'success', message: t('committee.messages.created') })
        }}
      />

      <EndTermModal
        isOpen={!!endingMember}
        member={endingMember}
        onClose={() => setEndingMember(null)}
        onConfirm={handleEndTerm}
        loading={endTerm.isPending}
      />
    </>
  )
}
